/**
 * The kit, when the staff under the caret is a percussion staff.
 *
 * Typing a letter already writes the drum nearest that line, which covers the kick, the
 * snare and the toms. The cymbals are what this is for: the closed and the open hi-hat are
 * written in the same place and told apart by the little circle over the open one, so the
 * only way to say which one a note is, is to name it. The list is the part's own kit rather than a fixed table --
 * a score can carry any kit, and a file written elsewhere brings its own.
 *
 * It acts on the selection, as the rest of the palette below it does. With nothing selected
 * the editing layer refuses in words and the page shows them, which is the same answer every
 * other button here gives.
 */

import { drums } from "@app/score-editor";
import type { NoteHead } from "@app/score-editor";
import { Kicker } from "./Page";
import type { ScoreActions } from "../lib/score-actions";

/** The head each drum is drawn with, as close as a line of text can get to it. */
const HEADS: Record<NoteHead, string> = {
  normal: "●",
  x: "✕",
  "circle-x": "⊗",
  diamond: "◆",
  triangle: "▲",
  slash: "／",
};

export interface ScorePaletteDrumsProps {
  readonly actions: ScoreActions;
}

export default function ScorePaletteDrums({ actions }: ScorePaletteDrumsProps): JSX.Element | null {
  const { score, selection, caret } = actions;
  const kit = drums.kitOf(score, caret?.partId ?? null) ?? drums.kitInSelection(score, selection);
  if (kit === null) return null;

  const sounding = new Set(drums.drumsInSelection(score, selection));

  return (
    <div className="space-y-1.5">
      <Kicker>{kit.name}</Kicker>
      <div className="space-y-1">
        {kit.notes.map((drum) => {
          const on = sounding.has(drum.name);
          const look = on
            ? "border-ink bg-ink text-white"
            : "border-rule text-ink-faded hover:border-ink-quiet hover:text-ink";
          return (
            <button
              key={drum.name}
              type="button"
              title={`${drum.name} (${drum.midi})`}
              aria-pressed={on}
              onClick={() => actions.edit(drums.setDrum(score, selection, drum.name), drum.name)}
              className={`flex w-full items-center gap-2 rounded-[8px] border px-2 py-1 text-left text-[12px] ${look}`}
            >
              <span className="w-5 text-center text-[11px] leading-none">
                {drum.open ? `${HEADS[drum.notehead]}\u00b0` : HEADS[drum.notehead]}
              </span>
              <span className="truncate">{drum.name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
